import { motion } from "framer-motion";
import { Banknote } from "lucide-react";
import SceneShell from "../components/SceneShell";
import CountUp from "../components/CountUp";
import MoneyFlow from "../components/MoneyFlow";

/**
 * Scene 41 — "£400 a month × 500 branches = £200,000 / month."
 */

export default function Scene41_TwoHundredK() {
  return (
    <SceneShell variant="ink" align="center" data-testid="scene-200k-month">
      <MoneyFlow density={28} color="rgba(233,168,32,0.85)" symbol="£" />

      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at center, rgba(233,168,32,0.18) 0%, transparent 55%)",
            "radial-gradient(ellipse at center, rgba(233,168,32,0.30) 0%, transparent 55%)",
            "radial-gradient(ellipse at center, rgba(233,168,32,0.18) 0%, transparent 55%)",
          ],
        }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full text-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="kn-eyebrow mx-auto flex items-center justify-center gap-2"
        >
          <Banknote className="w-3.5 h-3.5" style={{ color: "#e9a820" }} />
          Per month
        </motion.div>

        {/* Equation */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-8 inline-flex items-center gap-3 sm:gap-5 rounded-full px-7 py-4 glass-dark"
        >
          <Token value="500 branches" />
          <span className="text-2xl text-white/40">×</span>
          <Token value="£400 / mo" />
          <span className="text-2xl text-white/40">=</span>
        </motion.div>

        {/* Big number */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7, filter: "blur(28px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.5, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10"
          data-testid="200k-monthly-number"
        >
          <div
            className="kn-display tnum leading-[0.85]"
            style={{
              fontSize: "clamp(100px, 17vw, 250px)",
              background:
                "linear-gradient(135deg, #d4940a 0%, #e9a820 50%, #d4940a 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              textShadow: "0 0 100px rgba(233,168,32,0.45)",
              whiteSpace: "nowrap",
            }}
          >
            <CountUp
              from={0}
              to={200000}
              prefix="£"
              duration={2.4}
              startDelay={2.0}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 3.0 }}
          className="mt-6 kn-display text-3xl sm:text-5xl lg:text-6xl text-white"
        >
          <span style={{ color: "#e9a820" }}>per month.</span>
        </motion.div>
      </div>
    </SceneShell>
  );
}

function Token({ value }) {
  return (
    <span
      className="kn-display tnum text-base sm:text-xl whitespace-nowrap"
      style={{ color: "rgba(255,255,255,0.85)", fontWeight: 700 }}
    >
      {value}
    </span>
  );
}
